import { DEFAULT_LANGUAGE } from '../../../i18n/constants';
import type { Category } from '../../../stores/categories/CategoriesStore';
import { readDevOverride } from '../../../stores/jackpot/devOverride';

// LAST-RESORT FALLBACK, used only when core does not send `eligibleCategoryIds` at all.
//
// Deliberately narrow: player-backend still serves the legacy Crownslots jackpot feed, so a lobby row
// named "Jackpots"/"Jackpot Slots" may already exist for those games. Matching bare /jackpot/ would
// hang the Crown Jackpot amount off that unrelated row.
const JACKPOT_CATEGORY_NAME_PATTERN = /crown\s*jackpot/i;

/**
 * Dev-build-only escape hatch for when the rule table has no CATEGORY row for this environment yet:
 * set it to the row's exact default-language name (case-insensitive) to attach the header there.
 *
 *   localStorage.setItem('debug:jackpot_category', 'Jackpot Games')
 *   localStorage.removeItem('debug:jackpot_category')   // back to the configured rules
 *
 * List the lobby's row names with:
 *   [...document.querySelectorAll('[data-games-row]')].map((el) => el.dataset.gamesRow)
 */
export const JACKPOT_CATEGORY_OVERRIDE_KEY = 'debug:jackpot_category';

/**
 * Is this lobby row the jackpot collection?
 *
 * Answered by config, not by content: a row is the jackpot collection exactly when its
 * `masterGameSubCategoryId` appears in `splash_jackpot.jackpot_eligible_slot_rule` as CATEGORY +
 * INCLUDE, forwarded here as `eligibleCategoryIds` (KRA-303). Same table the jackpot's own game
 * eligibility is configured in, so the badged row cannot drift away from the games that contribute.
 *
 * @param eligibleCategoryIds configured ids, or `undefined` when core did not answer with the field.
 *   An empty array means NO row is configured — the correct answer is then `false` for every row, and
 *   the name pattern must NOT step in and badge one anyway.
 */
export function isJackpotCategory(
  category: Pick<Category, 'name' | 'masterGameSubCategoryId'>,
  eligibleCategoryIds?: number[],
): boolean {
  const name = category.name?.[DEFAULT_LANGUAGE] ?? '';
  const override = readDevOverride(JACKPOT_CATEGORY_OVERRIDE_KEY)?.trim();

  // The override still wins, so a dev can attach the header to any row regardless of the config.
  if (override) {
    return name.toLowerCase() === override.toLowerCase();
  }

  if (eligibleCategoryIds) {
    return eligibleCategoryIds.includes(Number(category.masterGameSubCategoryId));
  }

  return JACKPOT_CATEGORY_NAME_PATTERN.test(name);
}
